// Small pieces both ParkConnect pages share: a labelled field, an inline note,
// the document picker, and the lot list.

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, DocIcon, SpinnerIcon, UploadIcon } from './Icons';
import { uploadDoc } from './api';

export function Field({ label, hint, optional, children }) {
  return (
    <label className="pc-field">
      <span className="pc-label">
        {label}
        {optional && <span className="pc-opt"> · optional</span>}
      </span>
      {children}
      {hint && <span className="pc-hint">{hint}</span>}
    </label>
  );
}

export function Note({ kind = 'info', icon, children }) {
  return <div className={`pc-note ${kind}`}>{icon}<div>{children}</div></div>;
}

/**
 * Picks one file and uploads it immediately, so the form only ever carries the
 * stored `{ url, name }`. Uploading on pick rather than on submit means a slow
 * connection blocks the file chooser, not the Submit button — and a submission
 * that fails validation doesn't re-send the image.
 */
export function DocUpload({ kind, value, onChange, label, sub, accept = 'image/*,application/pdf' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [over, setOver] = useState(false);

  const take = useCallback(async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const stored = await uploadDoc(file, kind);
      onChange({ url: stored.url, name: stored.name || file.name });
    } catch (e) {
      setErr(e.message || 'That upload did not go through.');
    } finally {
      setBusy(false);
    }
  }, [kind, onChange]);

  const cls = ['pc-drop'];
  if (over) cls.push('over');
  if (value) cls.push('done');

  return (
    <div>
      <button
        type="button" className={cls.join(' ')} disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer?.files?.[0]); }}
      >
        <span className="pc-drop-icon">
          {busy ? <SpinnerIcon size={20} /> : value ? <CheckIcon size={20} /> : <UploadIcon size={20} />}
        </span>
        <span style={{ minWidth: 0 }}>
          <span className="pc-drop-name">{busy ? 'Uploading…' : value ? value.name : label}</span>
          <span className="pc-drop-sub">{value ? 'Attached — tap to replace' : sub}</span>
        </span>
        {value && !busy && <DocIcon size={16} style={{ marginLeft: 'auto', flex: 'none' }} />}
      </button>
      <input
        ref={inputRef} type="file" accept={accept} hidden
        onChange={e => { take(e.target.files?.[0]); e.target.value = ''; }}
      />
      {err && <p className="pc-hint" style={{ color: '#b3261e' }}>{err}</p>}
    </div>
  );
}

// The lots a permit is actually issued for. Read from the pathfinder's own
// snapshot so this list can't drift from the map, with a hardcoded fallback for
// when that file isn't reachable (a stale build, or the API host being hit
// directly). Bus stops live in the same file and are filtered out — you cannot
// hold a permit for one.
const FALLBACK_LOTS = ['A', 'AC', 'ACE', 'ACW', 'ALC', 'Animal Science', 'B', 'D', 'F',
  'Freedman Crescent', 'G', 'H', 'I', 'L', 'M', 'N', 'Parkade', 'Physical Plant Stores',
  'Q', 'R', 'RCFTR', 'Robson Hall', 'SD', "St. John's", 'U', 'V', 'W', 'X', 'Y'];

export function useLots() {
  const [lots, setLots] = useState(FALLBACK_LOTS);
  useEffect(() => {
    let live = true;
    fetch('/Map Info/pathfinder/parking_lots.json')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('no snapshot'))))
      .then(rows => {
        if (!live || !Array.isArray(rows)) return;
        const names = rows
          .map(r => r?.lot)
          .filter(name => typeof name === 'string' && name && !/bus stop$/i.test(name));
        if (names.length) setLots(names);
      })
      .catch(() => { /* the fallback list is already in state */ });
    return () => { live = false; };
  }, []);
  return lots;
}
